/* eslint-disable no-underscore-dangle */
import Vue from 'vue';

import moment from 'moment';
import VueParticles from 'vue-particles';
import VueI18n from 'vue-i18n'; // TODO: Use Vuetify built in i18n plugins
import VeeValidate from 'vee-validate';
import VueSanitize from 'vue-sanitize';
import VueCookies from 'vue-cookies';
import validationMessagesEn from 'vee-validate/dist/locale/en';
import validationMessagesFr from 'vee-validate/dist/locale/fr';
import DaySpanVuetify from 'dayspan-vuetify-2';
import VeeValidateCustomRules from '@/utilities/veeValidatorCustomRules';
import toast from '@/utilities/toast';
import VueTelInput from 'vue-tel-input';
import 'vue-tel-input/dist/vue-tel-input.css';

import VueApollo from 'vue-apollo';

// Import for project files
import App from '@/App.vue';
import store from '@/store/index';
import router from '@/routes/router';
import Requests from '@/api/requests';
import Filters from '@/utilities/filters';

// UI Components
import Navbar from '@/components/shared/navigation/TheNavbar';
import Footer from '@/components/shared/TheFooter';
import Tree from '@/components/sites/Tree';
import ContainerProbeList from '@/components/shared/widgets/forms/VuetifyInputs/ContainerProbeList.vue';

// Custom vue plugins
import VueShepherd from 'vue-shepherd';
import VueYoutube from 'vue-youtube';
import VueApexCharts from 'vue-apexcharts';
import VueTimepicker from 'vue2-timepicker';
import periodicFetchPlugin from '@/utilities/periodicFetchPlugin';
import { SocketClient } from '@/utilities/socket';
import apolloClient from './helpers/graphql/apollo-client';
import { loadLocaleMessages } from './locales/i18n';
import UtilsPlugin from './services/utils';
import vuetify from './plugins/vuetify';
import './plugins/mutation_events';

import 'vue2-timepicker/dist/VueTimepicker.css';

// vue directives
import './utilities/directives';

/**
 * Plugins
 */
Vue.use(VueI18n);
Vue.use(VueShepherd);
// Homemade Plugins
Vue.use(Filters);
Vue.use(Requests);

// Cookies config
Vue.use(VueCookies);
VueCookies.config('7d');
// inject some dependency into vuex store


// Vue Material UI Framework
Vue.use(VueParticles);
// Vue Sanitize
Vue.use(VueSanitize, {
    allowedTags: [],
    allowedAttributes: {},
});

Vue.use(VueApollo);

const apolloProvider = new VueApollo({
    defaultClient: apolloClient,
});
Vue.use(UtilsPlugin);
Vue.use(VueYoutube);
Vue.use(VueApexCharts);
// Telephone options
const options = {
    validCharactersOnly: true,
    preferredCountries: ['CA', 'US'],
    invalidMsg: 'phone not valid',
};
Vue.use(VueTelInput, options);
Vue.use(periodicFetchPlugin);

Vue.component('apexchart', VueApexCharts);
Vue.component('navbar', Navbar);
Vue.component('custom-footer', Footer);
Vue.component('Tree', Tree);
Vue.component('ContainerProbeList', ContainerProbeList);

// calendar  TODO : remove this if unused?
Vue.use(DaySpanVuetify, {
    methods: {
        getDefaultEventColor: () => '#1976d2',
    },
});

// Async function to fetch locale messages, initialize i18n, and instantiate the Vue app.
async function initializeApp() {
    const messages = await loadLocaleMessages();

    const initialLocale = navigator.language.split('-')[0].toLowerCase();

    // Initialize VueI18n with loaded messages
    const i18n = new VueI18n({
        locale: initialLocale || 'fr',
        fallbackLocale: 'fr',
        silentFallbackWarn: true,
        messages,
    });

    store.$i18n = i18n;

    // Set up Vue.prototype extensions
    Vue.prototype.moment = moment;
    Vue.prototype.$tourGuide = new Vue.prototype.$shepherd({
        useModalOverlay: true,
        cancelIcon: {
            enabled: true,
        },
        classes: 'shepherd-theme-custom',
        scrollTo: { behavior: 'smooth', block: 'center' },
    });

    // Use VeeValidate with i18n
    Vue.use(VeeValidate, {
        i18nRootKey: 'validations',
        i18n,
        dictionary: {
            en: validationMessagesEn, // Ensure these are imported or defined
            fr: validationMessagesFr,
        },
        events: '',
        fieldsBagName: 'formFields',
    });
    const googleMapsApiKey = process.env.VUE_APP_GOOGLE_MAP_API;
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&loading=async&callback=initMap&libraries=places`;
    script.async = true;
    document.head.appendChild(script);

    script.onload = () => {
        console.log('Google Maps API script loaded successfully');
    };

    script.onerror = () => {
        console.error('Failed to load the Google Maps API script');
    };
    // Event listener for the multi tab logout
    window.addEventListener('storage', event => {
        if (event.key === 'logoutEvent') {
            window.location.href = '/login'; // Redirect all tabs to login page
        } else if (event.key === 'loginEvent') {
            window.location.reload(); // Reload to apply login on every tabs
        }
    });

    // Restore auth state from cookies before mounting the app
    // This ensures isLoggedIn is set correctly before router guards run
    await store.dispatch('checkAuthOnAppInit');

    const socketClient = new SocketClient();
    await socketClient.connect();

    // Instantiate and mount the Vue app
    const app = new Vue({
        toast,
        router,
        VeeValidateCustomRules,
        store,
        vuetify,
        i18n,
        apolloProvider,
        VueTimepicker,
        render: h => h(App),
    }).$mount('#app');

    // Make the app available globally for Cypress tests
    if (window.Cypress) {
        window.app = app;
    }
}

// Execute app initialization
initializeApp()
    .then(() => {
        // eslint-disable-next-line no-console
        console.log('End app initialization');
    })
    .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Failed to initialize the Vue app:', error);
    });
